import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { apiCall } from '../../api';

const MyDeletionRequests = ({ userRole, orgId, getToken, onBack, onHome, onEditProfile }) => {
  const { user, logout } = useAuth0();
  const { t } = useTranslation('myDeletionRequests');
  const { t: tc } = useTranslation('common');

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    if (!profileOpen) return;
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen]);

  const handleLogout = () => {
    setProfileOpen(false);
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleEditProfile = () => {
    setProfileOpen(false);
    if (onEditProfile) onEditProfile();
  };

  const fetchRequests = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError('');
    try {
      const data = await apiCall(
        `/api/expenses/deletion-requests/my/?org_id=${orgId}&status=ALL`,
        {},
        getToken
      );
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to fetch deletion requests');
    } finally {
      setLoading(false);
    }
  }, [orgId, getToken]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'bg-amber-100 text-amber-800';
      case 'APPROVED': return 'bg-green-100 text-green-800';
      case 'REJECTED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PENDING': return '⏳';
      case 'APPROVED': return '✓';
      case 'REJECTED': return '✗';
      default: return '';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center gap-3">
        <button onClick={onHome} aria-label="Home" title="Home" className="text-blue-600 hover:text-blue-700 flex-shrink-0">
          <svg width="20" height="20" viewBox="0 0 24 24" className="fill-current">
            <path d="M12 3.2 3 10v10h6v-6h6v6h6V10l-9-6.8Z" />
          </svg>
        </button>
        <button onClick={onBack} aria-label="Back" className="text-blue-600 hover:text-blue-700">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M5 12l7-7M5 12l7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <span className="text-sm font-medium text-gray-900 flex-1">{t('title')}</span>
        <div className="flex-1" />
        <div ref={profileRef} className="relative">
          <button
            onClick={() => setProfileOpen((open) => !open)}
            aria-label="Account menu"
            aria-expanded={profileOpen}
            className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium"
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </button>
          {profileOpen && (
            <div className="absolute right-0 top-11 w-64 bg-white rounded-xl shadow-lg border border-gray-100 p-4 z-50">
              <p className="text-sm text-gray-900 truncate">{user?.email}</p>
              <p className="text-sm text-gray-400 truncate mb-4">{user?.email}</p>
              <button
                onClick={handleEditProfile}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition mb-2"
              >
                {tc('editProfile')}
              </button>
              <button
                onClick={handleLogout}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
              >
                {tc('logout')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-sm mx-auto px-4 py-8">
        <h1 className="text-center text-base font-medium text-blue-600 mb-6 uppercase tracking-wider">
          {t('title')}
        </h1>

        {loading ? (
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
            <p className="text-sm text-gray-500">{t('loading')}</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
            <p className="text-sm text-gray-500">{t('noRequests')}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {requests.map(request => (
              <div key={request.id} className="bg-white border border-gray-200 rounded-lg p-3.5">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {request.transaction_name || `${request.transaction_type} #${request.transaction_id}`}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {request.transaction_type === 'expense' ? t('expense') : t('contribution')}
                      {request.amount && ` — ₹${Number(request.amount).toFixed(2)}`}
                    </p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${getStatusColor(request.status)}`}>
                    {getStatusIcon(request.status)} {request.status}
                  </span>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-2.5 mb-2">
                  <p className="text-xs text-gray-500 mb-1">
                    <strong className="text-gray-700">{t('requestedAt')}:</strong>{' '}
                    {request.requested_at ? new Date(request.requested_at).toLocaleDateString() : '—'}
                  </p>
                  {request.reason && (
                    <p className="text-xs text-gray-500 mb-1">
                      <strong className="text-gray-700">{t('reason')}:</strong> {request.reason}
                    </p>
                  )}
                  {request.status === 'REJECTED' && request.review_note && (
                    <p className="text-xs text-red-600 mt-1">
                      <strong>{t('rejectionReason')}:</strong> {request.review_note}
                    </p>
                  )}
                  {request.status === 'APPROVED' && request.reviewed_at && (
                    <p className="text-xs text-green-600 mt-1">
                      <strong>{t('approvedAt')}:</strong>{' '}
                      {new Date(request.reviewed_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyDeletionRequests;
